import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Calendar, Award } from 'lucide-react'; // Importando Award para o plano
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { useAuth } from '../../hooks/useAuth';

const ProfilePage: React.FC = () => {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  
  const userInitials = user?.email ? user.email.charAt(0).toUpperCase() : '?';
  // Usando i18n.language para formatar a data corretamente
  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString(i18n.language) : 'N/A';
  
  const getPlanDisplay = (status: string | undefined) => {
    if (!status) return 'N/A';
    switch (status) {
      case 'pro':
        return t('Plano Pro');
      case 'basic':
        return t('Plano Basic');
      case 'free':
        return t('Plano Gratuito');
      case 'inactive':
        return t('Plano Inativo');
      default:
        return status;
    }
  };
  
  const planDisplay = getPlanDisplay(profile?.subscription_status);
  const planColor = profile?.subscription_status === 'pro' ? 'text-primary' : 'text-gray-500 dark:text-gray-400';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="p-6 lg:p-10 max-w-4xl mx-auto flex flex-col gap-8"
    >
      <motion.h1 
        className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1, duration: 0.6 }}
      >
        {t('profile.title')}
      </motion.h1>
      
      <div className="card p-8 flex flex-col md:flex-row items-center gap-8">
        
        {/* Avatar/Initials */}
        <div className="flex items-center justify-center aspect-square bg-primary text-white font-bold rounded-full size-24 text-4xl shadow-lg shrink-0">
          {userInitials}
        </div>
        
        {/* Details */}
        <div className="flex flex-col gap-3 w-full">
          
          {/* Email */}
          <div className="flex items-center gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-3">
            <Mail className="size-5 text-gray-500 dark:text-gray-400" />
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('profile.email_label')}</p>
              <p className="text-lg font-semibold text-[#181411] dark:text-white">{user?.email || 'N/A'}</p>
            </div>
          </div>
          
          {/* Plan Status */}
          <div className="flex items-center gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-3">
            <Award className={`size-5 ${planColor}`} />
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('Plano de Assinatura')}</p>
              <p className={`text-lg font-semibold ${planColor}`}>{planDisplay}</p>
            </div>
          </div>
          
          {/* Member Since */}
          <div className="flex items-center gap-3">
            <Calendar className="size-5 text-gray-500 dark:text-gray-400" />
            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('profile.member_since_label')}</p>
              <p className="text-lg font-semibold text-[#181411] dark:text-white">{memberSince}</p>
            </div>
          </div>
        </div>
      </div>
      
      <motion.button
        onClick={() => console.log('Edição desabilitada')}
        disabled
        className="flex h-12 w-full md:w-auto cursor-not-allowed items-center justify-center overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700/50 text-[#181411] dark:text-gray-300 text-sm font-bold leading-normal tracking-[0.015em] px-6 opacity-50 mx-auto"
        whileHover={{ scale: 1 }}
        whileTap={{ scale: 1 }}
      >
        {t('profile.edit_button')}
      </motion.button>
    </motion.div>
  );
};

export default ProfilePage;